import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { Sparkles, ShieldCheck, Zap, MoveRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/v1`;

/**
 * Public landing page for referral QR codes and open-house handouts.
 * URL: /join/:code — no auth required.
 *
 * Design goal: an agent scanning a QR at an open house or clicking a text
 * from a colleague sees WHO invited them (name + brand color) before hitting
 * the signup form. Personalized referrals convert 3-4× better than cold links.
 */
export default function JoinReferral() {
  const { code } = useParams();
  const [ref, setRef] = useState(null);
  const [status, setStatus] = useState("loading"); // loading | ok | notfound

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/referrals/public/${code}`);
        setRef(r.data);
        setStatus("ok");
      } catch {
        setStatus("notfound");
      }
    })();
  }, [code]);

  if (status === "loading") {
    // Skeleton — paint the full layout with placeholder chips so the page
    // feels instant. The gradient hero + iKEY watermark + card outline are
    // all identical to the loaded state; only the referrer's specifics fade
    // in once /public/{code} returns.
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="relative" style={{ background: "linear-gradient(135deg, #1F6FEB 0%, #0f172a 100%)" }}>
          <div className="mx-auto max-w-4xl px-6 pt-14 pb-24 text-white">
            <div className="flex items-center gap-2 font-heading font-bold text-xl tracking-tight opacity-95">
              <img src="/ikey-logo.png" alt="iKEY" className="h-8 w-8 rounded-md bg-white/10 p-0.5" />
              iKEY
            </div>
            <div className="mt-14 flex flex-col md:flex-row md:items-center gap-6">
              <div className="h-24 w-24 rounded-full border-4 border-white/30 shadow-xl bg-white/10 backdrop-blur animate-pulse" data-testid="join-skeleton-photo" />
              <div className="min-w-0 space-y-3">
                <div className="text-xs font-medium uppercase tracking-widest opacity-70">You're invited by</div>
                <div className="h-10 w-64 rounded bg-white/20 animate-pulse" />
                <div className="h-4 w-40 rounded bg-white/15 animate-pulse" />
              </div>
            </div>
            <div className="mt-8 h-6 w-96 max-w-full rounded bg-white/15 animate-pulse" />
            <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur px-4 py-2 text-sm font-medium ring-1 ring-white/20">
              <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
              30 days free · no credit card required
            </div>
          </div>
        </div>
        <div className="mx-auto max-w-4xl px-6 -mt-14 relative z-10">
          <div className="rounded-xl bg-white shadow-2xl border border-slate-200 p-6 sm:p-10 space-y-4">
            <div className="h-6 w-1/2 rounded bg-slate-100 animate-pulse" />
            <div className="h-4 w-full rounded bg-slate-100 animate-pulse" />
            <div className="h-4 w-3/4 rounded bg-slate-100 animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  if (status === "notfound") {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6">
        <div className="max-w-md text-center">
          <div className="mx-auto h-14 w-14 rounded-lg flex items-center justify-center bg-slate-900 text-white">
            <img src="/ikey-logo.png" alt="iKEY" className="h-10 w-10 rounded-md" />
          </div>
          <h1 className="mt-6 font-heading text-3xl font-bold tracking-tight text-slate-900">This invite link expired</h1>
          <p className="mt-3 text-slate-600">The referral code <code className="font-mono text-sm">{code}</code> isn't active. You can still create an account and get started right away.</p>
          <Link to="/login" className="mt-6 inline-flex items-center gap-2 rounded-md bg-slate-900 px-5 py-3 text-sm font-semibold text-white hover:bg-slate-800" data-testid="join-fallback-signup">
            Create an account <MoveRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    );
  }

  const brand = ref.brand_color || "#1F6FEB";
  const firstName = (ref.referrer_name || "Your friend").split(" ")[0];
  const initials = (ref.referrer_name || "?").split(" ").map(s => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="min-h-screen bg-slate-50">
      <div
        className="relative"
        style={{ background: `linear-gradient(135deg, ${brand} 0%, #0f172a 100%)` }}
      >
        <div className="mx-auto max-w-4xl px-6 pt-14 pb-24 text-white">
          <div className="flex items-center gap-2 font-heading font-bold text-xl tracking-tight opacity-95">
            <img src="/ikey-logo.png" alt="iKEY" className="h-8 w-8 rounded-md bg-white/10 p-0.5" />
            iKEY
          </div>
          <div className="mt-14 flex flex-col md:flex-row md:items-center gap-6">
            {ref.photo_url ? (
              <img src={ref.photo_url} alt={ref.referrer_name} className="h-24 w-24 rounded-full object-cover border-4 border-white/30 shadow-xl" data-testid="join-referrer-photo" />
            ) : (
              <div className="h-24 w-24 rounded-full flex items-center justify-center text-3xl font-bold border-4 border-white/30 shadow-xl bg-white/10 backdrop-blur" data-testid="join-referrer-initials">
                {initials}
              </div>
            )}
            <div className="min-w-0">
              <div className="text-xs font-medium uppercase tracking-widest opacity-70">You're invited by</div>
              <h1 className="mt-1 font-heading text-4xl sm:text-5xl font-bold tracking-tighter" data-testid="join-referrer-name">{ref.referrer_name}</h1>
              {ref.license_number && (
                <div className="mt-2 text-sm opacity-80">Licensed Realtor · #{ref.license_number}</div>
              )}
              {ref.logo_url && (
                <div className="mt-3 inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur px-3 py-1 ring-1 ring-white/20" data-testid="join-referrer-brokerage">
                  <img src={ref.logo_url} alt="" className="h-4 w-4 rounded object-contain bg-white/80 p-0.5" />
                  <span className="text-xs font-medium opacity-90">Brokerage-verified</span>
                </div>
              )}
            </div>
          </div>
          <p className="mt-8 text-xl sm:text-2xl leading-snug max-w-2xl opacity-95 font-heading">
            "{firstName} thinks iKEY will save you hours every week — and gave you a link so you can try it free."
          </p>
          <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-white/15 backdrop-blur px-4 py-2 text-sm font-medium ring-1 ring-white/20" data-testid="join-trial-badge">
            <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
            30 days free · no credit card required
          </div>
        </div>
      </div>

      <div className="mx-auto max-w-4xl px-6 -mt-14 relative z-10">
        <div className="rounded-xl bg-white shadow-2xl border border-slate-200 p-6 sm:p-10">
          <div className="text-xs font-medium uppercase tracking-widest text-slate-500">What is iKEY?</div>
          <h2 className="mt-2 font-heading text-2xl sm:text-3xl font-bold tracking-tighter text-slate-900">
            The CRM that tells you who to call today.
          </h2>
          <div className="mt-8 grid sm:grid-cols-3 gap-6">
            <Perk icon={Sparkles} title="Consistent lead ranking" body="Not vibes. Real signals: recency, budget clarity, timeline, consent — ranked the same way every time." />
            <Perk icon={ShieldCheck} title="Professional compliance deadlines" body="Set an acceptance date and every inspection, appraisal, and financing deadline auto-generates." />
            <Perk icon={Zap} title="Client portal built-in" body="Every buyer gets a private magic-link portal — no SMS ping-pong, no lost documents." />
          </div>

          <div className="mt-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <Link
              to={`/login?ref=${ref.referral_code}`}
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-md px-6 py-4 text-base font-semibold text-white shadow-sm hover:opacity-95 transition-opacity"
              style={{ background: brand }}
              data-testid="join-signup-btn"
            >
              Start my 30-day free trial <MoveRight className="h-5 w-5" />
            </Link>
            <div className="text-xs text-slate-500 text-center sm:text-left sm:max-w-xs leading-relaxed">
              Free for the first 30 days. No credit card required. When you subscribe, {firstName} gets a free month too.
            </div>
          </div>
        </div>

        <div className="mt-6 text-center text-xs text-slate-500 pb-12">
          Already have an account? <Link to={`/login?ref=${ref.referral_code}`} className="font-medium text-slate-700 hover:underline">Sign in</Link>
        </div>
      </div>
    </div>
  );
}

function Perk({ icon: Icon, title, body }) {
  return (
    <div>
      <div className="h-9 w-9 rounded-md bg-slate-100 flex items-center justify-center">
        <Icon className="h-4 w-4 text-slate-700" />
      </div>
      <div className="mt-3 font-heading font-semibold text-slate-900">{title}</div>
      <p className="mt-1 text-sm text-slate-600 leading-relaxed">{body}</p>
    </div>
  );
}
